import { useState } from 'react';
import { Link as LucideLink, Database, Globe, CheckCircle2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import TenantMatcher from './TenantMatcher';
import { syncTenantIds, syncTenantIdsTAM, TAMSyncResponse } from '../../services/api';

interface SyncResult {
  source: 'partner-center' | 'tam';
  success: boolean;
  message: string;
  details?: TAMSyncResponse;
}

export default function TenantMatcherPage() {
  const [syncingPC, setSyncingPC] = useState(false);
  const [syncingTAM, setSyncingTAM] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSyncPartnerCenter = async () => {
    setSyncingPC(true);
    setSyncResult(null);
    try {
      const result = await syncTenantIds();
      setSyncResult({
        source: 'partner-center',
        success: true,
        message: `Partner Center: Updated ${result.summary.clientsUpdated} clients from ${result.partnerCenterCustomers} customers`
      });
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      setSyncResult({
        source: 'partner-center',
        success: false,
        message: 'Failed to sync from Partner Center'
      });
    } finally {
      setSyncingPC(false);
    }
  };

  const handleSyncTAM = async () => {
    setSyncingTAM(true);
    setSyncResult(null);
    setShowDetails(false);
    try {
      const result = await syncTenantIdsTAM();
      setSyncResult({
        source: 'tam',
        success: true,
        message: `TAM: Updated ${result.summary.clientsUpdated} clients from ${result.summary.tamMappings} TAM mappings`,
        details: result
      });
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      const error = err as Error;
      setSyncResult({
        source: 'tam',
        success: false,
        message: error.message?.includes('TAM not configured')
          ? 'TAM not configured — set TAM_API_URL and TAM_API_KEY in Azure Function App settings'
          : 'Failed to sync from TAM'
      });
    } finally {
      setSyncingTAM(false);
    }
  };

  const isSyncing = syncingPC || syncingTAM;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <LucideLink className="h-7 w-7 text-primary" />
            Tenant Matcher
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Link Pax8 clients to their Microsoft 365 tenant IDs
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSyncTAM}
            disabled={isSyncing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md shadow-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <Database className={`h-4 w-4 ${syncingTAM ? 'animate-pulse' : ''}`} />
            {syncingTAM ? 'Syncing TAM...' : 'Sync from TAM'}
          </button>
          <button
            onClick={handleSyncPartnerCenter}
            disabled={isSyncing}
            className="btn-secondary flex items-center gap-2"
          >
            <Globe className={`h-4 w-4 ${syncingPC ? 'animate-spin' : ''}`} />
            {syncingPC ? 'Syncing...' : 'Sync from Partner Center'}
          </button>
        </div>
      </div>

      {/* Sync Result */}
      {syncResult && (
        <div className={`rounded-lg border ${
          syncResult.success
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {syncResult.success
                ? <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                : <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              }
              <span className={syncResult.success
                ? 'text-green-800 dark:text-green-200'
                : 'text-red-800 dark:text-red-200'
              }>
                {syncResult.message}
              </span>
            </div>
            {syncResult.details && (
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 flex items-center gap-1 text-sm"
              >
                {showDetails ? 'Hide' : 'Details'}
                {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            )}
          </div>

          {/* TAM Sync Details */}
          {showDetails && syncResult.details && (
            <div className="border-t border-green-200 dark:border-green-800 p-4 space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white/50 dark:bg-white/5 rounded-md p-2 text-center">
                  <div className="text-lg font-bold text-foreground">{syncResult.details.summary.tamMappings}</div>
                  <div className="text-xs text-muted-foreground">TAM Mappings</div>
                </div>
                <div className="bg-white/50 dark:bg-white/5 rounded-md p-2 text-center">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">{syncResult.details.summary.clientsUpdated}</div>
                  <div className="text-xs text-muted-foreground">Updated</div>
                </div>
                <div className="bg-white/50 dark:bg-white/5 rounded-md p-2 text-center">
                  <div className="text-lg font-bold text-muted-foreground">{syncResult.details.summary.clientsSkipped.split(' ')[0]}</div>
                  <div className="text-xs text-muted-foreground">Already Linked</div>
                </div>
                <div className="bg-white/50 dark:bg-white/5 rounded-md p-2 text-center">
                  <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{syncResult.details.summary.clientsNotMatched.split(' ')[0]}</div>
                  <div className="text-xs text-muted-foreground">No Match</div>
                </div>
              </div>

              {/* Match Breakdown */}
              {syncResult.details.summary.clientsUpdated > 0 && (
                <div>
                  <div className="text-sm font-medium text-foreground mb-1">Match Types</div>
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-2 py-0.5 rounded">
                      Exact: {syncResult.details.matchBreakdown.exact}
                    </span>
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded">
                      Clean: {syncResult.details.matchBreakdown.clean}
                    </span>
                    <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-2 py-0.5 rounded">
                      Partial: {syncResult.details.matchBreakdown.partial}
                    </span>
                  </div>
                </div>
              )}

              {/* Matched Clients List */}
              {syncResult.details.matchedClients.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-foreground mb-1">
                    Matched Clients ({syncResult.details.matchedClients.length})
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {syncResult.details.matchedClients.map((m, i) => (
                      <div key={i} className="text-xs flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span className="font-medium text-foreground">{m.name}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-mono text-[10px]">{m.tenantId.substring(0, 8)}...</span>
                        {m.domain && <span className="text-blue-600 dark:text-blue-400">{m.domain}</span>}
                        <span className={`px-1 rounded text-[10px] ${
                          m.matchType === 'exact' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                          m.matchType === 'clean' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                        }`}>{m.matchType}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Clients */}
              {syncResult.details.unmatchedClients.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-foreground mb-1">
                    Unmatched Clients ({syncResult.details.unmatchedClients.length})
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-1">
                      {syncResult.details.unmatchedClients.map((name, i) => (
                        <span key={i} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                  {syncResult.details.note && (
                    <p className="text-xs text-muted-foreground mt-1 italic">{syncResult.details.note}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="card p-6">
        <TenantMatcher key={refreshKey} onRefresh={() => setRefreshKey(prev => prev + 1)} />
      </div>

      {/* Help Section */}
      <div className="card p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">How it works</h3>
        <ol className="list-decimal list-inside text-sm text-blue-800 dark:text-blue-200 space-y-1">
          <li><strong>Sync from TAM</strong> (recommended) — pulls tenant mappings from Tenant Access Manager, our centralized registry</li>
          <li><strong>Sync from Partner Center</strong> — fetches CSP customer data from Microsoft Partner Center</li>
          <li>For any remaining unmatched clients, manually select the correct tenant from the dropdown and click "Link"</li>
        </ol>
      </div>
    </div>
  );
}
